import React from 'react';
type Tome = {
  number: string;
  title: string;
  meta: string;
  framework: string;
  promise: string;
  badge: string;
  contents: string[];
};
const tomes: Tome[] = [{
  number: '01',
  title: 'First Seat',
  meta: 'Levels 1–3 · 10 sessions',
  framework: 'The Paid Table Loop · The Open Door Ending',
  promise: 'Intro one-shots that turn drop-in players into regulars.',
  badge: 'bg-sage',
  contents: ['Ten intro one-shots, from The Lantern Toll to The Apprentice Who Vanished', 'The 90-second welcome that sets tone, pace and table rules', 'Five pre-gen concepts a first-timer can pick up in two minutes', 'The 15-minute VTT tech check before strangers log in', 'Five first-session mistakes that lose paying players, and the fix for each', 'The Lantern Toll at levels 1, 3 and 5: the same session in three tiers']
}, {
  number: '02',
  title: 'Session Zero & the Opening Arc',
  meta: 'Levels 1–4 · 8 sessions',
  framework: 'The Table Charter · The Spotlight Rotation',
  promise: 'Launch a campaign paying players commit to.',
  badge: 'bg-plum',
  contents: ['A 60-minute session zero, minute by minute, word for word', 'Two four-session opening arcs: The Ashen Road and Crown of Tidewater', 'Tone & Content Dial: agree on horror, romance, gore and difficulty before play', 'Absent-player protocols: six in-story reasons a character steps away', 'The 3-minute “previously on” recap that re-hooks the table', 'Drop either arc into your own world in 15 minutes']
}, {
  number: '03',
  title: 'The Weekly Engine',
  meta: 'Levels 3–7 · 10 sessions',
  framework: 'The Episode Socket · The Cliffhanger Ladder',
  promise: 'Drop-in episodes that plug into any campaign you’re already running.',
  badge: 'bg-coral',
  contents: ['Ten episodes: a heist, a chase, a siege, a tribunal, a haunted manor and more', 'Socket Sheet: connect any episode to your campaign in 10 minutes', 'Cliffhanger Ladder bank: 20 endings sorted by rung', 'Reskin guide: turn each episode into three different stories', 'Emergency picker: the right episode when you have 20 minutes', 'The Monday-night 20-minute prep ritual for a weekly table']
}, {
  number: '04',
  title: 'Mid-Campaign Arcs',
  meta: 'Levels 5–10 · 10 sessions',
  framework: 'The Three-Clock Arc · The Faction Web',
  promise: 'Factions, clocks and twists that keep paying players hooked for months.',
  badge: 'bg-gold-deep',
  contents: ['Two five-session arcs: The Salt Throne and The Hollow Saints', 'Clock Board: villain, world and party clocks on one page', 'Faction reaction table: what each faction does after every player action', 'Twist delivery: the exact beats for a reveal that lands', 'The month-three check: six questions to ask before the table drifts', '3- and 7-session versions of either arc']
}, {
  number: '05',
  title: 'Special Tables',
  meta: 'Levels 1–5 · 10 sessions',
  framework: 'The Audience Dial · The Two-Hour Compression',
  promise: 'Ready sessions for corporate events, birthdays, families and conventions.',
  badge: 'bg-smoke',
  contents: ['Ten event sessions: corporate, birthday, family, kids 8–12, convention, seasonal', 'Teaching 5E in five minutes: the only rules guests need', 'Eight simplified pre-gen cards for total beginners', 'Event-day checklist and a minute-by-minute timeline worksheet', 'Kid-table tone guide: non-lethal stakes, scary-but-safe', 'Getting eight first-time guests into a VTT in 10 minutes']
}, {
  number: '06',
  title: 'Finales & Next Season',
  meta: 'Levels 8–12 · 9 sessions',
  framework: 'The Payoff Map · The Season Bridge',
  promise: 'Land the ending and launch the next campaign.',
  badge: 'bg-plum-mid',
  contents: ['The four-session Eclipse Gate finale, plus three standalone high-level sessions', 'Callback Ledger: every promise, secret and grudge to pay off', 'Boss phase templates and lair events that change the fight every round', 'An epilogue session and a Season Two premiere', 'Next-Season Pitch Board: three premises for the table to vote on', 'When a key player can’t make the finale: three ways to keep their character in the ending']
}];
export function Tomes() {
  return <section aria-labelledby="tomes-heading" className="w-full bg-cream" data-id="element-301" data-mp="3pzsb4rcsnq0v">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-302" data-mp="8r59vzk8ymyk">
        <div className="text-center" data-id="element-303" data-mp="8qn7smawleiq">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-gold-deep" data-id="element-304" data-mp="3s2ez7xzdx4b">
            The complete year
          </p>
          <h2 id="tomes-heading" className="mt-3 font-display font-semibold text-plum text-3xl sm:text-5xl" data-id="element-305" data-mp="2ajjpgrzbjfbt">
            Six volumes. <span className="text-gold-deep" data-id="element-306" data-mp="l1efswxyn1pl">Fifty-seven sessions.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-307" data-mp="l3qwl6rzf612">
            Each volume covers one stage of a paid table, from a stranger’s
            first seat to next season’s premiere, and works on its own. Every
            session is world-agnostic: it drops into your own setting.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-308" data-mp="2adkciw1toas">
          {tomes.map(tome => <article key={tome.number} className="flex flex-col rounded-3xl border border-cream-dark bg-white p-6 shadow-card" data-id="element-309" data-mp="6cqpb52z1i6i">
              <div className="flex items-center gap-2.5" data-id="element-310" data-mp="3th6jlh7a7dco">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full font-display text-base font-semibold text-white ${tome.badge}`} data-id="element-311" data-mp="3thq2p18o47sa">
                  {tome.number}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-muted" data-id="element-312" data-mp="1q8o4bqgi7x4a">
                  Volume {tome.number} · {tome.meta}
                </span>
              </div>
              <h3 className="mt-3 font-display text-xl font-semibold leading-snug text-plum" data-id="element-313" data-mp="1gai2t9ym0szy">
                {tome.title}
              </h3>
              <p className="mt-1 text-[12px] font-semibold uppercase tracking-wider text-gold-deep" data-id="element-314" data-mp="1bcgjcnx9urny">
                {tome.framework}
              </p>
              <p className="mt-2 text-sm text-ink-soft" data-id="element-315" data-mp="29106od75qyef">{tome.promise}</p>
              <ul className="mt-4 grid gap-2 border-t border-cream-dark pt-4" data-id="element-316" data-mp="240uhr4s9vq01">
                {tome.contents.map(item => <li key={item} className="flex gap-2 text-[13px] text-ink" data-id="element-317" data-mp="1ua99sqqpl94g">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-gold" data-id="element-318" data-mp="1u86m4dd13s2w" />
                    {item}
                  </li>)}
              </ul>
            </article>)}
        </div>
      </div>
    </section>;
}